/**
 * Cart drawer cross-sell ("Also buy with").
 *
 * Products come from, in order:
 *   1. complementary product recommendations for the first cart line
 *   2. related product recommendations for the first cart line
 *   3. the fallback products picked in the theme settings
 *
 * Recommendations are configured in the Shopify Search & Discovery app.
 * See snippets/cart-drawer-cross-sell.liquid for the markup.
 */

const CART_CROSS_SELL_CACHE = new Map();
const CART_CROSS_SELL_CACHE_TTL = 10 * 60 * 1000;
const CART_CROSS_SELL_RELOAD_DELAY = 350;

function cartCrossSellFormatMoney(cents, format) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  if (cents === null || cents === undefined || isNaN(cents)) return '';

  const formatString = format || '${{amount}}';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const placeholder = formatString.match(placeholderRegex);

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    const parts = (number / 100).toFixed(precision).split('.');
    const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  if (!placeholder) return formatWithDelimiters(cents, 2);

  let value;
  switch (placeholder[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return formatString.replace(placeholderRegex, value);
}

class CartCrossSell extends HTMLElement {
  constructor() {
    super();
    this.loadToken = 0;
    this.items = [];
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.list = this.querySelector('[data-list]');
    this.status = this.querySelector('[data-status]');
    this.limit = Math.max(1, parseInt(this.dataset.limit, 10) || 3);
    this.manualProducts = this.parseJSON(this.dataset.manualProducts, []);
    this.addLabel = this.dataset.addLabel || 'Add';
    this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
    this.errorMessage = this.dataset.errorMessage || 'Something went wrong. Please try again.';
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.emptyCartFallback = this.dataset.emptyCartFallback !== 'false';

    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);

    this.reload();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        clearTimeout(this.reloadTimer);
        // An emptied cart switches to the manual list right away so the drawer
        // doesn't sit on stale recommendations.
        if (!this.getCartProducts().length) {
          this.reload();
          return;
        }
        this.reloadTimer = setTimeout(() => this.reload(), CART_CROSS_SELL_RELOAD_DELAY);
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    clearTimeout(this.reloadTimer);
  }

  parseJSON(value, fallback) {
    try {
      const parsed = JSON.parse(value || '');
      return Array.isArray(parsed) ? parsed : fallback;
    } catch (error) {
      return fallback;
    }
  }

  escape(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /** Products currently in the cart, in the order they are displayed. */
  getCartProducts() {
    const drawer = this.closest('cart-drawer');
    const rows = drawer ? drawer.querySelectorAll('#CartDrawer-CartItems [data-product-id]') : [];
    return Array.from(rows)
      .map((row) => ({ productId: Number(row.dataset.productId) }))
      .filter((item) => item.productId);
  }

  async reload() {
    const cartProducts = this.getCartProducts();
    const cartProductIds = new Set(cartProducts.map((item) => item.productId));
    const isCartEmpty = cartProducts.length === 0;
    const token = ++this.loadToken;

    let pool;

    if (isCartEmpty) {
      if (!this.emptyCartFallback) {
        // Setting disabled: an empty cart keeps the standard empty state.
        this.hide();
        return;
      }
      // No product to anchor recommendations on, so only the products picked
      // in the theme settings can be shown.
      pool = this.manualProducts;
    } else {
      const anchorProductId = cartProducts[0].productId;
      const requestLimit = Math.min(10, this.limit + cartProductIds.size + 2);
      pool = await this.buildPool(anchorProductId, requestLimit);
      if (token !== this.loadToken) return;
    }

    const items = this.pickProducts(pool, cartProductIds);
    if (!items.length) {
      this.hide();
      return;
    }

    this.render(items, isCartEmpty);
  }

  pickProducts(pool, cartProductIds) {
    const seen = new Set(cartProductIds);
    const items = [];

    for (const product of pool) {
      if (items.length >= this.limit) break;
      if (!product || !product.id) continue;

      const productId = Number(product.id);
      if (seen.has(productId) || product.available === false) continue;
      if ((product.title || '').toLowerCase().includes('shipping protection')) continue;

      const variants = (product.variants || []).filter((variant) => variant && variant.id);
      if (!variants.length || !variants.some((variant) => variant.available)) continue;

      seen.add(productId);
      items.push(product);
    }

    return items;
  }

  async buildPool(anchorProductId, requestLimit) {
    const [complementary, related] = await Promise.all([
      this.fetchRecommendations(anchorProductId, 'complementary', requestLimit),
      this.fetchRecommendations(anchorProductId, 'related', requestLimit),
    ]);

    return [...complementary, ...related, ...this.manualProducts];
  }

  async fetchRecommendations(productId, intent, limit) {
    const cacheKey = `${productId}:${intent}:${limit}`;
    const cached = CART_CROSS_SELL_CACHE.get(cacheKey);
    if (cached && Date.now() - cached.time < CART_CROSS_SELL_CACHE_TTL) return cached.products;

    const baseUrl = this.dataset.recommendationsUrl || '/recommendations/products';
    const url = `${baseUrl}.json?product_id=${productId}&limit=${limit}&intent=${intent}`;

    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) {
        CART_CROSS_SELL_CACHE.set(cacheKey, { time: Date.now(), products: [] });
        return [];
      }

      const data = await response.json();
      const products = Array.isArray(data.products) ? data.products : [];
      CART_CROSS_SELL_CACHE.set(cacheKey, { time: Date.now(), products });
      return products;
    } catch (error) {
      return [];
    }
  }

  render(items, fillsEmptyCart = false) {
    this.items = items;
    this.list.innerHTML = items.map((product) => this.renderItem(product)).join('');
    this.hidden = false;

    // With no cart items to list, the drawer keeps its regular layout (totals,
    // checkout) and this list takes the place of the cart items.
    const drawer = this.closest('cart-drawer');
    if (drawer) drawer.classList.toggle('cart-drawer--has-cross-sell', fillsEmptyCart);
    if (fillsEmptyCart) this.refocusOpenDrawer();
  }

  /**
   * Removing the last cart item traps focus in the empty state, which gets
   * hidden once the manual list replaces it. Move focus back to the drawer.
   */
  refocusOpenDrawer() {
    const drawer = this.closest('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) return;

    const drawerElement = document.getElementById('CartDrawer');
    const focusElement = drawer.querySelector('.drawer__inner');
    if (drawerElement && focusElement && typeof trapFocus === 'function') {
      trapFocus(drawerElement, focusElement);
    }
  }

  renderItem(product) {
    const variants = (product.variants || []).filter((variant) => variant && variant.id);
    const availableVariants = variants.filter((variant) => variant.available);
    const defaultVariant = availableVariants[0] || variants[0];

    const title = this.escape(product.title);
    const url = this.escape(product.url || '#');
    const image = product.featured_image
      ? `<img src="${this.escape(product.featured_image)}" alt="${title}" loading="lazy" width="128" height="128">`
      : '<span class="cart-cross-sell__image-placeholder" aria-hidden="true"></span>';

    const price = Number(defaultVariant.price != null ? defaultVariant.price : product.price || 0);
    const compareAt = Number(defaultVariant.compare_at_price || 0);

    return `
      <li class="cart-cross-sell__item" data-product-id="${Number(product.id)}" data-variant-id="${Number(defaultVariant.id)}">
        <a class="cart-cross-sell__image-link" href="${url}" tabindex="-1" aria-hidden="true">${image}</a>
        <div class="cart-cross-sell__details">
          <a class="cart-cross-sell__title" href="${url}">${title}</a>
          <div class="cart-cross-sell__variant">${this.renderVariantPicker(variants, defaultVariant, product)}</div>
          <p class="cart-cross-sell__error" role="alert" data-error hidden></p>
        </div>
        <div class="cart-cross-sell__side">
          <div class="cart-cross-sell__price" aria-live="polite" data-price>${this.renderPrice(price, compareAt)}</div>
          <button
            type="button"
            class="button cart-cross-sell__add"
            data-add
            aria-label="${this.escape(this.addLabel)} ${title}"
            ${availableVariants.length ? '' : 'disabled'}
          >
            <span aria-hidden="true">+</span>
            <span>${this.escape(this.addLabel)}</span>
          </button>
        </div>
      </li>
    `;
  }

  renderVariantPicker(variants, defaultVariant, product) {
    if (variants.length < 2) {
      return `<span class="cart-cross-sell__variant-name">${this.escape(defaultVariant.title)}</span>`;
    }

    const options = variants
      .map((variant) => {
        const label = variant.available ? variant.title : `${variant.title} (${this.soldOutLabel})`;
        return `<option value="${Number(variant.id)}"${variant.available ? '' : ' disabled'}${
          variant.id === defaultVariant.id ? ' selected' : ''
        }>${this.escape(label)}</option>`;
      })
      .join('');

    return `<select class="cart-cross-sell__select" data-variant-select aria-label="${this.escape(
      product.title
    )}">${options}</select>`;
  }

  renderPrice(price, compareAt) {
    const current = `<span class="cart-cross-sell__price-value">${cartCrossSellFormatMoney(price, this.moneyFormat)}</span>`;
    if (compareAt > price) {
      return `<s class="cart-cross-sell__price-compare">${cartCrossSellFormatMoney(compareAt, this.moneyFormat)}</s>${current}`;
    }
    return current;
  }

  hide() {
    this.hidden = true;
    this.items = [];
    if (this.list) this.list.innerHTML = '';

    const drawer = this.closest('cart-drawer');
    if (drawer) drawer.classList.remove('cart-drawer--has-cross-sell');
  }

  onChange(event) {
    const select = event.target.closest('[data-variant-select]');
    if (!select) return;

    const row = select.closest('.cart-cross-sell__item');
    const product = this.items.find((item) => String(item.id) === row.dataset.productId);
    if (!product) return;

    const variant = (product.variants || []).find((item) => String(item.id) === select.value);
    if (!variant) return;

    row.dataset.variantId = variant.id;
    row.querySelector('[data-price]').innerHTML = this.renderPrice(
      Number(variant.price || 0),
      Number(variant.compare_at_price || 0)
    );
    row.querySelector('[data-add]').disabled = !variant.available;
  }

  onClick(event) {
    const button = event.target.closest('[data-add]');
    if (!button) return;

    const row = button.closest('.cart-cross-sell__item');
    const variantId = Number(row.dataset.variantId);
    if (!variantId) return;

    this.addToCart(row, button, variantId);
  }

  async addToCart(row, button, variantId) {
    const errorElement = row.querySelector('[data-error]');
    if (errorElement) errorElement.hidden = true;

    button.disabled = true;
    button.classList.add('is-loading');

    const cartDrawer = this.closest('cart-drawer');
    const formData = new FormData();
    formData.append('id', String(variantId));
    formData.append('quantity', '1');

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      formData.append(
        'sections',
        cartDrawer.getSectionsToRender().map((section) => section.id)
      );
    }
    formData.append('sections_url', window.location.pathname);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    try {
      const response = await fetch(window.routes.cart_add_url, config);
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        this.showError(row, data.description || this.errorMessage);
        return;
      }

      if (cartDrawer) {
        // Same as product-form.js: the drawer may have been rendered in its
        // empty state before this add, so the empty layout has to be dropped.
        cartDrawer.classList.remove('is-empty', 'cart-drawer--has-cross-sell');
        if (typeof cartDrawer.renderContents === 'function') cartDrawer.renderContents(data);
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-cross-sell' });
      }
    } catch (error) {
      this.showError(row, this.errorMessage);
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  }

  showError(row, message) {
    const errorElement = row.querySelector('[data-error]');
    if (errorElement) {
      errorElement.textContent = message;
      errorElement.hidden = false;
    }
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}
